import { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, Upload, File } from "lucide-react";
import { StipInstructionsContent } from "@/components/StipInstructionsContent";
import { SiteLegalFooter } from "@/components/layout/site-legal-footer";

interface UploadLinkInfo {
  organization_logo_url?: string | null;
  application: {
    internal_id: string;
    equipment_name: string;
    equipment_price: string | null;
  };
  stips: Array<{
    id: number;
    template_name: string;
    template_description: string;
    instructions?: string;
    has_document: boolean;
    plaid_url?: string | null;
  }>;
  expires_at: string;
}

export function PublicUploadPage() {
  const { token } = useParams<{ token: string }>();
  const [uploadingStips, setUploadingStips] = useState<{ [key: number]: boolean }>({});
  const [uploadedStips, setUploadedStips] = useState<{ [key: number]: boolean }>({});
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const [draggingOverStip, setDraggingOverStip] = useState<number | null>(null);

  // Fetch upload link info
  const { data: uploadInfo, isLoading, error, refetch } = useQuery({
    queryKey: ["upload-link", token],
    queryFn: async () => {
      const { data } = await customInstance.get<UploadLinkInfo>(`/api/public/uploads/${token}/`);
      return data;
    },
    retry: false,
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ stipId, files }: { stipId: number; files: File[] }) => {
      const formData = new FormData();
      // Append all files
      files.forEach(file => {
        formData.append('files', file);
      });
      formData.append('stip_id', stipId.toString());
      const { data } = await customInstance.post(`/api/public/uploads/${token}/upload/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    },
    onSuccess: (data, variables) => {
      setUploadedStips(prev => ({ ...prev, [variables.stipId]: true }));
      
      if (data.documents) {
        const uploadedCount = data.documents.filter((d: any) => d.status === 'uploaded').length;
        const duplicateCount = data.documents.filter((d: any) => d.status === 'duplicate').length;
        
        if (uploadedCount > 0) {
          toast.success(`Successfully uploaded ${uploadedCount} document(s) for "${uploadInfo?.stips.find(s => s.id === variables.stipId)?.template_name}"`);
        }
        if (duplicateCount > 0) {
          toast.info(`${duplicateCount} document(s) already exist`);
        }
        if (data.errors && data.errors.length > 0) {
          toast.error(`Some files failed: ${data.errors.join('; ')}`);
        }
      } else if (data.status === 'duplicate') {
        toast.info(data.message || 'This file already exists');
      } else if (data.message) {
        toast.success(data.message);
      }
      
      // Refetch to update has_document status
      refetch();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Failed to upload documents");
    },
    onSettled: (data, error, variables) => {
      setUploadingStips(prev => ({ ...prev, [variables.stipId]: false }));
      // Reset file input
      if (fileInputRefs.current[variables.stipId]) {
        fileInputRefs.current[variables.stipId]!.value = '';
      }
    },
  });

  // File validation helper
  const validateFiles = (files: File[]): { validFiles: File[]; errors: string[] } => {
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
    const allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif'];
    const validFiles: File[] = [];
    const errors: string[] = [];
    
    for (const file of files) {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File exceeds 25MB limit`);
        continue;
      }
      
      // Check file type
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        errors.push(`${file.name}: Invalid file type. Allowed: ${allowedTypes.join(', ')}`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    return { validFiles, errors };
  };

  const handleFileSelect = (stipId: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const { validFiles, errors } = validateFiles(files);
    
    // Show errors
    errors.forEach(error => toast.error(error));
    
    if (validFiles.length === 0) return;

    setUploadingStips(prev => ({ ...prev, [stipId]: true }));
    uploadMutation.mutate({ stipId, files: validFiles });
  };

  // Drag and drop handlers for each stip
  const handleDragEnter = (e: React.DragEvent, stipId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingOverStip(stipId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to null if we're leaving the drop zone itself, not a child element
    if (e.currentTarget === e.target) {
      setDraggingOverStip(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent, stipId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingOverStip(null);
    
    if (uploadingStips[stipId]) return;
    
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    
    const { validFiles, errors } = validateFiles(files);
    
    // Show errors
    errors.forEach(error => toast.error(error));
    
    if (validFiles.length === 0) return;
    
    setUploadingStips(prev => ({ ...prev, [stipId]: true }));
    uploadMutation.mutate({ stipId, files: validFiles });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
        <SiteLegalFooter openPrivacyInNewTab />
      </div>
    );
  }

  if (error || !uploadInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Upload Link Invalid</h1>
                <p className="text-gray-600">
                  {error && (error as any)?.response?.data?.error
                    ? (error as any).response.data.error
                    : "This upload link is invalid or has expired."}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        <SiteLegalFooter openPrivacyInNewTab />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 p-4">
      <div className="max-w-3xl mx-auto">
        {/* Logo: dealer branding when org has uploaded a logo (same resolution as public apply). */}
        <div className="flex justify-center mb-8">
          <img
            src={uploadInfo.organization_logo_url || "/logo-full.png"}
            alt={uploadInfo.organization_logo_url ? "Organization logo" : "DealerDog"}
            className="max-h-48 max-w-lg w-auto object-contain"
          />
        </div>

        {/* Application Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Application Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-gray-500">Deal ID</Label>
                <p className="text-lg font-semibold">{uploadInfo.application.internal_id}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Equipment Name</Label>
                <p className="text-lg font-semibold">{uploadInfo.application.equipment_name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-sm text-gray-500">Equipment Price</Label>
                <p className="text-lg font-semibold">
                  {uploadInfo.application.equipment_price 
                    ? `$${parseFloat(uploadInfo.application.equipment_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stips Upload Card */}
        <Card>
          <CardHeader>
            <CardTitle>Required Documents</CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Please upload the requested documents below. You can upload multiple documents as needed.
            </p>
          </CardHeader>
          <CardContent>
            {uploadInfo.stips.length > 0 ? (
              <div className="space-y-4">
                {uploadInfo.stips.map((stip) => (
                  <div
                    key={stip.id}
                    onDragEnter={(e) => handleDragEnter(e, stip.id)}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, stip.id)}
                    className={`border-2 rounded-lg p-4 transition-colors ${
                      draggingOverStip === stip.id
                        ? 'border-blue-500 bg-blue-50 border-dashed'
                        : stip.has_document
                        ? 'bg-green-50 border-green-200'
                        : 'bg-white border-gray-200'
                    } ${uploadingStips[stip.id] ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">{stip.template_name}</h3>
                          {stip.has_document && (
                            <div className="flex items-center gap-1 text-green-600 text-sm">
                              <CheckCircle2 className="w-4 h-4" />
                              <span>Uploaded</span>
                            </div>
                          )}
                        </div>
                        {stip.template_description && (
                          <p className="text-sm text-gray-600 mb-3">{stip.template_description}</p>
                        )}
                        {(stip.instructions || "").trim() !== "" && (
                          <details className="mb-3 rounded border border-gray-200 bg-white px-2 py-1 text-sm">
                            <summary className="cursor-pointer font-medium text-gray-800">See instructions</summary>
                            <div className="mt-2 border-t border-gray-100 pt-2">
                              <StipInstructionsContent content={stip.instructions || ""} />
                            </div>
                          </details>
                        )}
                        <div className="space-y-2">
                          {stip.plaid_url && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Button
                                type="button"
                                onClick={() => window.open(stip.plaid_url as string, "_blank", "noopener,noreferrer")}
                              >
                                Share statements from your bank
                              </Button>
                              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                Faster
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 flex-wrap">
                            <input
                              ref={(el) => (fileInputRefs.current[stip.id] = el)}
                              type="file"
                              multiple
                              onChange={(e) => handleFileSelect(stip.id, e)}
                              disabled={uploadingStips[stip.id]}
                              className="hidden"
                              id={`file-input-${stip.id}`}
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                            />
                            <Button
                              onClick={() => fileInputRefs.current[stip.id]?.click()}
                              disabled={uploadingStips[stip.id]}
                              variant={stip.has_document || stip.plaid_url ? "outline" : "default"}
                              size="sm"
                              className="flex items-center gap-2"
                            >
                              {uploadingStips[stip.id] ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Uploading...
                                </>
                              ) : stip.has_document ? (
                                <>
                                  <Upload className="w-4 h-4" />
                                  Replace File
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4" />
                                  Upload Document
                                </>
                              )}
                            </Button>
                            {draggingOverStip === stip.id && (
                              <div className="text-blue-600 text-sm font-medium">
                                Drop files here to upload
                              </div>
                            )}
                          </div>
                          {!draggingOverStip && !uploadingStips[stip.id] && (
                            <p className="text-xs text-gray-500">
                              Click "Upload Document" or drag and drop files here
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No documents required at this time.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Info Footer */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Link expires: {new Date(uploadInfo.expires_at).toLocaleDateString()}</p>
        </div>
        <SiteLegalFooter
          openPrivacyInNewTab
          className="sticky bottom-0 mt-4 bg-orange-50/95 border-t border-orange-100/80 backdrop-blur-sm"
        />
      </div>
    </div>
  );
}

